// Display labels for Hades. The VALUES are defined in
// cerberus/cerberus/hades/config.yaml (queues, work_categories) and stored as
// written; only the English lives here.
//
// A queue added to config.yaml with no entry here still renders — label() falls
// back to the raw key rather than dropping it.

export const QUEUE_LABELS = {
  // Not a verdict — the fleet has not reached this requirement yet. Given a
  // label and a rail slot so it is countable and filterable beside the real
  // queues, rather than being an invisible absence.
  not_investigated: 'Not investigated',
  unnoticed: 'Unnoticed',
  active: 'Active',
  awaiting_deploy: 'Awaiting deploy',
  recently_completed: 'Recently completed',
  shipped_record_stale: 'Shipped / record stale',
  necessary_unvoiced: 'Necessary / unvoiced',
  dormant: 'Dormant',
  irrelevant: 'Irrelevant',
  nice_to_have: 'Nice to have',
  needs_evidence: 'Unknown',
};

// Rail order is BY WHAT DEMANDS ATTENTION, not alphabetical and not by count.
// Shipped/stale first because it is the only closeable queue; Unnoticed second
// because it is the failure the system exists to surface; Unknown third because
// a rising count there means retrieval is degrading.
// NOT_INVESTIGATED IS DELIBERATELY ABSENT. It is not a verdict — it is the
// absence of one, so listing it beside real queues made "we haven't looked"
// compete with "we looked and concluded X". It is a checkbox on the rail
// instead (Support_Hades.showUninvestigated).
export const QUEUE_ORDER = [
  'shipped_record_stale', 'unnoticed', 'needs_evidence', 'active',
  'awaiting_deploy', 'recently_completed', 'necessary_unvoiced',
  'nice_to_have', 'dormant', 'irrelevant',
];

// The one queue with evidence strong enough to act on: delivery is a deploy
// state AND the record is still open, so a cited PR sits behind it.
// recently_completed often has no delivery proof; irrelevant needs a human to
// confirm the duplicate's target first.
export const CLOSEABLE_QUEUES = ['shipped_record_stale'];

export const CATEGORY_LABELS = {
  customer_feature: 'Customer feature',
  bug_fix: 'Bug fix',
  app_stability_reliability: 'Reliability',
  architecture_platform: 'Architecture',
  data_integration: 'Data integration',
  security_compliance: 'Security',
  internal_tooling: 'Internal tooling',
  documentation: 'Documentation',
  maintenance: 'Maintenance',
  unclassified: 'Unclassified',
};

// The one axis that had no labels, which is why the distinction it carries was
// invisible. The `active` queue is DEFINED in cerberus/hades/config.yaml as four
// of these collapsed into one bucket — active_development, in_review,
// blocked_on_kairon, blocked_on_customer_or_vendor — so "the Active queue does
// not differentiate between in progress and not started" (Cindy, 2026-09-03
// blitz) was never a missing verdict. It was a rendered one short of a label.
//
// An un-investigated row has no attention value at all and must render NOTHING
// here, not 'Unknown': the row already says "Not investigated", and a second
// empty judgment beside it reads as a judgment that was made.
export const ATTENTION_LABELS = {
  none_found: 'No activity found',
  owned_idle: 'Owned, idle',
  active_development: 'Active development',
  in_review: 'In review',
  blocked_on_kairon: 'Blocked on Kairon',
  blocked_on_customer_or_vendor: 'Blocked on customer / vendor',
  recently_completed: 'Recently completed',
  unknown: 'Unknown',
};

export const LINKAGE_LABELS = {
  direct_customer_demand: 'Direct customer demand',
  indirect_customer_value: 'Indirect customer value',
  necessary_unvoiced: 'Necessary, unvoiced',
  no_established_link: 'No established link',
  unknown: 'Unknown',
};

export const DELIVERY_LABELS = {
  not_started: 'Not started',
  partial: 'Partial',
  implemented_unmerged: 'Implemented, unmerged',
  in_review: 'In review',
  merged_not_deployed: 'Merged, not deployed',
  deployed_unverified: 'Deployed',
  deployed_customer_confirmed: 'Deployed, confirmed',
  regressed: 'Regressed',
  closed_unmerged: 'Closed unmerged',
  unknown: 'Unknown',
};

// PRIORITY IS NOT DEFINED HERE. It lives in ../support_constants.js —
// priorityToCode() folds the pre-0664 strings (urgent/high/medium/low) onto the
// canonical p0-p3/none, PRIORITY_SHORT_LABELS renders them for a dense cell,
// PRIORITY_LABELS for a chart legend, and prioritySortValue() ranks them with
// 'none' after p3. Hades re-derived all four and had drifted from none of them;
// keeping one copy is what stops the next divergence.

export const AXIS_LABELS = {
  validity: 'Validity',
  demand: 'Demand',
  delivery: 'Delivery',
  attention: 'Attention',
  evidence_quality: 'Evidence',
};

export const label = (map, key) => map[key] || key || '—';

// ONE COMPARATOR FOR BOTH TABLES, so their sort semantics cannot drift.
//
// THE RULE THAT MATTERS: MISSING VALUES SORT LAST IN BOTH DIRECTIONS. A null is
// not "smaller" — it is absent, and a row with no owner or no merge date is not
// a legitimate answer to "show me the oldest". 22 of 357 PRs have no merged_at
// and 81 of 404 requirements have no owner; letting those float to the top of an
// ascending sort buries the rows actually being asked for.
//
// Returns a comparator, not a sorted array, so callers keep control of the copy
// (never sort a prop or a computed's source array in place — Vue reactivity
// makes that an infinite update loop).
export const comparator = (key, direction = 'asc', accessors = {}) => {
  const dir = direction === 'desc' ? -1 : 1;
  const read = accessors[key] || ((row) => row[key]);
  const missing = (v) => v === null || v === undefined || v === '';

  return (a, b) => {
    const av = read(a);
    const bv = read(b);
    // Absent last regardless of direction, and stable against each other.
    if (missing(av) && missing(bv)) return 0;
    if (missing(av)) return 1;
    if (missing(bv)) return -1;
    if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
    // localeCompare with numeric:true so '#9' sorts before '#10' — a plain
    // string compare puts 10 before 9 and makes an ID column read as noise.
    return String(av).localeCompare(String(bv), undefined, { numeric: true }) * dir;
  };
};
